import { useEffect } from "react";
import { toast } from "sonner";
import { logClientError } from "@/lib/error-logger";
import { Sentry } from "@/lib/sentry";
import { isExpectedOperationalError } from "@/lib/sentry-noise";
import { isLockStealMessage } from "@/lib/lock-steal";

/**
 * Hook that sets up global error handling for unhandled promise rejections
 * and runtime errors, surfacing them as toast notifications and logging to DB.
 */
export function useGlobalErrorHandler() {
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const message =
        event.reason?.message ||
        (typeof event.reason === "string" ? event.reason : "An unexpected error occurred");

      // Don't toast for auth-related or wallet/Web3 errors (not user-actionable)
      if (message.includes("JWT") || message.includes("refresh_token")) return;
      // Multi-tab Web Locks arbitration (supabase auth token refresh) is benign
      // and self-healing; useAuth owns the user-facing state for it. Never
      // surface the raw browser message as a toast or log it to Sentry/DB
      // (mirrors the unconditional drop in src/lib/sentry.ts, CR-DASHBOARD-3K/3J).
      if (isLockStealMessage(message)) return;
      if (message.includes("MetaMask") || message.includes("ethereum") || message.includes("web3")) return;

      // Suppress expected edge-function failures during QA chaos runs
      const chaosRunning = (window as unknown as { __qaChaosRunning?: boolean }).__qaChaosRunning;
      if (chaosRunning && (message.includes("Edge function returned") || message.includes("FunctionsHttpError") || message.includes("FunctionsFetchError"))) return;

      // Edge-function rejections that bubble all the way to window.unhandledrejection
      // are call-site bugs (missing await/.catch), not user-actionable errors. Each
      // call site already reports through its own UI surface (toast in mutations,
      // skeleton in queries). Reporting here just creates a noisy CR-DASHBOARD-11
      // group with one stack frame and no usable context.
      if (
        message.includes("FunctionsHttpError") ||
        message.includes("FunctionsFetchError") ||
        message.includes("Edge Function returned a non-2xx")
      ) return;

      // Expected operational states (plan/usage/rate limits, billing, 402/429)
      // are normal conditions: tell the user, but don't report to Sentry / DB.
      if (isExpectedOperationalError(event.reason)) {
        toast.error(message.length > 120 ? message.slice(0, 120) + "…" : message);
        return;
      }

      Sentry.captureException(event.reason);
      logClientError({
        severity: "error",
        message,
        stack: event.reason?.stack,
        metadata: { type: "unhandled_rejection" },
      });

      toast.error(message.length > 120 ? message.slice(0, 120) + "…" : message);
    };

    const handleGlobalError = (event: ErrorEvent) => {
      if (isExpectedOperationalError(event.error ?? event.message)) return;
      if (event.error) Sentry.captureException(event.error);
      logClientError({
        severity: "error",
        message: event.message || "Unknown runtime error",
        stack: event.error?.stack,
        metadata: {
          type: "runtime_error",
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleGlobalError);
    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      window.removeEventListener("error", handleGlobalError);
    };
  }, []);
}
